'use client';

import { Reveal } from '@/components/products/Reveal';
import { roadmapThemes, roadmapDisclaimer } from '@/lib/corporate-content';

/**
 * Roadmap / use-of-funds themes.
 *
 * The framing matters more than the design here: these are directions, not
 * commitments. No amount is named, no timeline is promised, and the
 * disclaimer is rendered as visible page content — not a tooltip, not a
 * footnote — so it cannot be read past.
 *
 * Layout family: a wide numbered ledger. Deliberately the plainest section on
 * the page; a forward-looking list should not be dressed up.
 */
export function RoadmapSection() {
  return (
    <section
      className="self-styled relative z-20 border-b border-white/[0.07]"
      aria-labelledby="roadmap-heading"
    >
      <div className="content-padding">
        <div className="grid grid-cols-1 gap-10 pb-12 pt-24 md:pt-32 lg:grid-cols-[1fr_1fr] lg:gap-20">
          <Reveal>
            <span className="eyebrow">Where investment goes</span>
            <h2
              id="roadmap-heading"
              className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
              style={{ fontSize: 'clamp(2.75rem, 6.5vw, 6rem)' }}
            >
              Roadmap themes
            </h2>
          </Reveal>
          <Reveal delay={90}>
            <p className="max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg md:leading-relaxed">
              These are the areas we would strengthen next with more capital and
              engineering capacity.
            </p>
          </Reveal>
        </div>

        <ol className="flex flex-col">
          {roadmapThemes.map((theme, i) => (
            <li key={theme.index}>
              <Reveal delay={i * 60}>
                <div className="grid grid-cols-1 gap-3 border-t border-white/10 py-8 md:grid-cols-[4rem_minmax(0,18rem)_1fr] md:items-baseline md:gap-8 md:py-10">
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/35">
                    {theme.index}
                  </span>
                  <h3 className="text-lg font-medium leading-snug text-white md:text-xl">
                    {theme.title}
                  </h3>
                  <p className="max-w-2xl text-sm font-light leading-relaxed text-white/55 md:text-[15px]">
                    {theme.body}
                  </p>
                </div>
              </Reveal>
            </li>
          ))}
        </ol>

        {/* The framing note — visible content, not fine print. */}
        <Reveal>
          <p
            className="mb-24 mt-10 max-w-3xl border-l py-1 pl-5 text-sm font-light leading-relaxed text-white/45 md:mb-32 md:text-[15px]"
            style={{ borderColor: 'var(--color-accent)' }}
          >
            {roadmapDisclaimer}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
